import { Checkbox, Form, Input, Tooltip, type TableColumnsType } from 'antd'
import { type Control, Controller, type UseFormRegister } from 'react-hook-form'
import { FaCheckCircle } from 'react-icons/fa'

import { DateFormatter, NumberFormatter } from '@/components/elements'
import { EMPTY_LABEL } from '@/constants'
import { ACTIONS, LABELS, STATUSES } from '@/constants/common/message.constants'
import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { getVisibleCustomKeySlots } from '@/features/custom-key-settings/models'
import { CargoWeightFormatter } from '@/features/shared/components'
import { type Sorter } from '@/features/shared/models'
import { SHIPMENT_ORDERS_SORT_KEYS } from '@/features/shared/models'
import { ShipmentCategoryTag } from '@/features/shipment-categories/components'
import { PackageCountFormatter } from '@/features/shipment-order-details/components'
import {
  EditableDeliveryStatusTag,
  ShipmentOrderStatusTypeTag
} from '@/features/shipment-orders/components'
import {
  type CustomKeySettingEntity,
  type ShipmentOrdersListExEntity
} from '@/libs/api'
import { type ShipmentDeliveryStatusType } from '@/libs/api/types'
import { createSortableColumns } from '@/utils/table.utils'

import { type BundleSelectionState } from '../../-models'
import { type ShipmentOrdersFormValues } from '../../-models/shipment-orders-form-values'

import { LEFT_FIXED_COLUMN_WIDTH } from './shipment-orders-table.constants'
import { WeightColumnTitle } from './WeightColumnTitle'

/**
 * まとめキー左のチェック（同じ束ねの依頼をまとめて行選択する操作）の描画に必要な情報。
 * 関連する3つを1つにまとめて受け取る（列定義の位置引数をこれ以上増やさないため）
 */
export type BundleSelectionProps = {
  /** 束ねごとの選択状態（画面に出ているメンバー基準） */
  stateByBundleId: Map<number, BundleSelectionState>
  /** 行選択が禁止されている間（編集中）はチェックも押せない */
  disabled: boolean
  onToggle: (record: ShipmentOrdersListExEntity) => void
}

export const createShipmentOrdersTableColumns = (
  // 配送依頼No は詳細遷移をやめ編集モーダルを開くため未使用（呼び出しの位置引数は維持）
  _tenantKey: string,
  sorter: Sorter,
  /**
   * 編集対象行の id → フォーム配列 index のマップ。
   * ピン留めで dataSource 上の並び (render の index) が動的に変わっても register 対象が
   * ぶれないよう、選択順に基づく安定した index を親から受け取る。編集中でない場合は空マップ。
   */
  editableRowIndexById: Map<number, number>,
  control: Control<ShipmentOrdersFormValues>,
  register: UseFormRegister<ShipmentOrdersFormValues>,
  onStatusChange?: (
    id: number,
    deliveryStatus: ShipmentDeliveryStatusType
  ) => Promise<void>,
  statusEditVariant?: 'clickable-tag' | 'tag-with-edit-icon',
  // 配送依頼No クリックで編集モーダルを開くためのハンドラ。
  // 未指定時は非活性テキスト表示（呼び出し側が編集フローを持たない場合のフォールバック）。
  onClickOrderNo?: (record: ShipmentOrdersListExEntity) => void,
  // まとめキー左のチェック（束ね単位の一括選択）。未指定時はチェックを描画しない
  bundleSelection?: BundleSelectionProps,
  // VOC_PJ-960 #4487: 拠点のカスタムキー設定（isVisible スロットのみ動的に列生成）
  customKeySettings?: CustomKeySettingEntity[]
): TableColumnsType<ShipmentOrdersListExEntity> =>
  createSortableColumns(
    [
      {
        title: LABELS.STATUS,
        key: 'status',
        width: LEFT_FIXED_COLUMN_WIDTH.STATUS,
        fixed: true,
        render: (_, record) =>
          onStatusChange ? (
            <EditableDeliveryStatusTag
              deliveryStatus={record.deliveryStatus}
              onChange={(newStatus) => onStatusChange(record.id, newStatus)}
              variant={statusEditVariant}
            />
          ) : (
            <ShipmentOrderStatusTypeTag statusType={record.status} />
          )
      },
      {
        // まとめキー（束ねの表示ラベル）。
        // 配送ステータスと配送依頼Noの間に置くため、左固定の並びに含める
        // （固定列の間に非固定列を挟むと横スクロール時に列が分離して描画される）
        title: LABELS.BUNDLE_LABEL,
        key: 'bundleLabel',
        width: LEFT_FIXED_COLUMN_WIDTH.BUNDLE_LABEL,
        fixed: true,
        render: (_, record) => {
          const { bundleId, bundleLabel } = record
          if (!bundleLabel) return ''
          // まとめキーは表示のみ（束ねからの分離は一覧の一括操作メニューが担う）
          const label = bundleLabel

          if (bundleId === null || !bundleSelection) return label

          // 同じまとめキーの依頼をまとめて行選択するチェック。画面に出ていないメンバーは
          // 選択状態を判定できないため、部分選択(indeterminate)から押した場合も
          // 「束ね全件を選択」に倒す（押下側の処理は呼び出し元が担う）
          const state = bundleSelection.stateByBundleId.get(bundleId) ?? 'none'
          return (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={state === 'all'}
                indeterminate={state === 'partial'}
                disabled={bundleSelection.disabled}
                onChange={() => bundleSelection.onToggle(record)}
                aria-label={ACTIONS.SELECT_BUNDLE_ORDERS}
              />
              {label}
            </div>
          )
        }
      },
      {
        title: LABELS.ORDER_NO,
        key: 'orderNo',
        width: LEFT_FIXED_COLUMN_WIDTH.ORDER_NO,
        fixed: true,
        render: (_, record) => {
          const formIndex = editableRowIndexById.get(record.id)
          return formIndex !== undefined ? (
            <>
              {/* { id: 1, orderNo: "1234" } の形式にするため、hiddenでidを用意しておく */}
              <input
                type="hidden"
                {...register(`shipmentOrders.${formIndex}.id`)}
                value={record.id || ''}
              />
              <Controller
                name={`shipmentOrders.${formIndex}.orderNo`}
                control={control}
                defaultValue={record.orderNo}
                rules={{
                  required: VALIDATION_MESSAGES.INPUT_ORDER_NO
                }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    validateStatus={error?.message ? 'error' : undefined}
                    help={error?.message}
                    className="m-0"
                  >
                    <Input
                      {...field}
                      placeholder={LABELS.ORDER_NO}
                      className="w-full"
                      id={field.name}
                    />
                  </Form.Item>
                )}
              />
            </>
          ) : (
            // 詳細モーダルへの遷移ではなく、その場で編集モーダルを開く
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent p-0 text-left text-blue-500"
              onClick={() => onClickOrderNo?.(record)}
            >
              {record.orderNo}
            </button>
          )
        }
      },
      {
        title: LABELS.SHIPPING_DATE,
        key: 'shippingDate',
        fixed: true,
        width: LEFT_FIXED_COLUMN_WIDTH.SHIPPING_DATE,
        render: (_, { group }) => (
          <DateFormatter dateFormat="yyyy/MM/dd" value={group.shippingDate} />
        )
      },
      {
        title: LABELS.DELIVERY_DATE,
        key: 'deliveryDate',
        fixed: true,
        width: LEFT_FIXED_COLUMN_WIDTH.DELIVERY_DATE,
        render: (_, { group }) => (
          <DateFormatter dateFormat="yyyy/MM/dd" value={group.deliveryDate} />
        )
      },
      {
        // 締日基準検索の結果確認用に請求計上月を月表示する（VOC_PJ-939 #4387）
        title: LABELS.INCOME_OPERATING_YEAR_MONTH,
        key: 'incomeOperatingYearMonth',
        width: 120,
        render: (_, { incomeOperatingYearMonth }) => (
          <DateFormatter
            dateFormat="yyyy/MM"
            value={incomeOperatingYearMonth}
          />
        )
      },
      {
        title: LABELS.CUSTOMER_CODE,
        key: 'customerCode',
        width: 120,
        render: (_, { group }) => group.customer.code
      },
      {
        title: LABELS.CUSTOMER,
        key: 'customer',
        width: 220,
        render: (_, { group }) => group.customer.name
      },
      {
        title: LABELS.DESIGNATED_PARTNER,
        key: 'partner',
        width: 220,
        render: (_, { partner }) => (
          <div className="line-clamp-2">{partner?.name ?? LABELS.EMPTY}</div>
        )
      },
      {
        title: LABELS.PARTNER,
        key: 'dispatchPartner',
        width: 220,
        render: (_, { dispatchDetails }) =>
          dispatchDetails?.[0]?.dispatch?.partner?.name ?? LABELS.EMPTY
      },
      {
        title: LABELS.SUB_PARTNER,
        key: 'dispatchSubPartner',
        width: 220,
        render: (_, { dispatchDetails }) =>
          dispatchDetails?.[0]?.dispatch?.subPartner?.name ?? LABELS.EMPTY
      },
      {
        title: LABELS.TRUCK_CODE,
        key: 'dispatchTruck',
        width: 100,
        render: (_, { dispatchDetails }) =>
          dispatchDetails?.[0]?.dispatch?.truck?.code ?? LABELS.EMPTY
      },
      {
        title: LABELS.SHIPMENT_CATEGORY,
        key: 'shipmentCategory',
        width: 120,
        render: (_, { group }) => (
          <ShipmentCategoryTag shipmentCategory={group.shipmentCategory} />
        )
      },
      {
        title: LABELS.ORIGIN,
        key: 'originSite',
        width: 380,
        render: (_, { group }) => (
          <>
            {group.originSite.name}
            <br />
            <span className="text-secondary">{group.originSite.code}</span>
          </>
        )
      },
      {
        title: LABELS.DESTINATION,
        key: 'destSite',
        width: 380,
        render: (_, { group }) => (
          <>
            {group.destSite.name}
            <br />
            <span className="text-secondary">{group.destSite.code}</span>
          </>
        )
      },
      {
        title: LABELS.PACKAGE_CATEGORY,
        key: 'packageCategory',
        width: 110,
        render: (_, { group }) => group.packageCategory?.name
      },
      {
        title: LABELS.PACKAGE_QUANTITY,
        key: 'packageCount',
        width: 110,
        onCell: () => ({
          className: 'text-right'
        }),
        render: (_, { packageCount }) => (
          <PackageCountFormatter value={packageCount} />
        )
      },
      {
        title: LABELS.QUANTITY,
        key: 'quantity',
        width: 100,
        onCell: () => ({
          className: 'text-right'
        }),
        render: (_, { quantity }) => <NumberFormatter value={quantity} />
      },
      {
        title: <WeightColumnTitle />,
        key: 'weight',
        width: 100,
        onCell: () => ({
          className: 'text-right'
        }),
        render: (_, { weight }) => <CargoWeightFormatter value={weight} />
      },
      {
        title: LABELS.ITEM_COUNT,
        key: 'detailCount',
        width: 100,
        onCell: () => ({
          className: 'text-right'
        }),
        render: (_, { detailCount }) => <NumberFormatter value={detailCount} />
      },
      {
        title: STATUSES.IS_MANUAL,
        key: 'isManual',
        width: 100,
        onCell: () => ({
          className: 'text-center'
        }),
        render: (_, { isManual }) =>
          isManual ? (
            <FaCheckCircle className="inline-block text-primary" />
          ) : null
      },
      {
        title: LABELS.NOTE,
        key: 'note',
        width: 400,
        render: (_, { note }) => <div className="line-clamp-2">{note}</div>
      },
      // VOC_PJ-960 #4487: 拠点で表示 ON のカスタムキーをスロット別に列生成（値は ShipmentOrder 直下）
      ...getVisibleCustomKeySlots(customKeySettings).map(
        ({ fieldName, label }) => ({
          title: label,
          key: fieldName,
          width: 140,
          render: (_: unknown, record: ShipmentOrdersListExEntity) => {
            const value = record[fieldName]
            return value ? (
              <Tooltip title={value}>
                <div className="line-clamp-2">{value}</div>
              </Tooltip>
            ) : (
              <div>{EMPTY_LABEL}</div>
            )
          }
        })
      )
    ],
    SHIPMENT_ORDERS_SORT_KEYS,
    sorter
  )
